/**
 * TokenSidePanel
 *
 * A reusable right-side panel that displays grouped merge tokens for insertion
 * into a rich text editor. Bundled with MergeFieldEditor by MergeFieldComposer.
 *
 * Each token shows:
 *   - A friendly label (bold)
 *   - The raw token string (mono, muted)
 *   - A short description of what it resolves to (muted)
 *
 * A search box filters the list (fuzzy). Clicking a token calls
 * `onInsert(token)` — the parent routes that to the editor via its ref.
 *
 * Scrolling: the panel owns its scroll region (the list is wrapped in a
 * ScrollArea). The root is `min-h-0 overflow-hidden` so it scrolls correctly
 * regardless of how a consumer sizes it, as long as it is placed in a bounded
 * flex context.
 */

import * as React from 'react';
import { cn } from '@/lib/utils';
import {
  ScrollArea,
  ScrollAreaViewport,
  ScrollAreaScrollbar,
} from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Braces, Lock, Search } from 'lucide-react';
import { fuzzyScore } from '@/services/mergeTokens';

// ============================================================================
// Types
// ============================================================================

export interface TokenItem {
  /** The raw token string, e.g. `{client.name}` or `{person}` */
  token: string;
  /** Friendly display label, e.g. "Client Name" */
  label: string;
  /** One-line human description of what this field resolves to */
  description: string;
  /** When true, the token is shown but greyed out (not available in this context) */
  dimmed?: boolean;
}

export interface TokenGroup {
  /** Group heading, e.g. "Client" or "Contextual" */
  label: string;
  /** Optional muted sub-description shown under the group heading */
  description?: string;
  /** Colour accent for the group heading pill */
  accent?: 'primary' | 'muted' | 'warning';
  tokens: TokenItem[];
}

export interface TokenSidePanelProps {
  groups: TokenGroup[];
  /** Called when a token is clicked */
  onInsert: (token: string) => void;
  /** Additional className for the outer container */
  className?: string;
  /** Show the raw `{token}` string under each label. Defaults to true. */
  showTokenString?: boolean;
  /** Show the search box. Defaults to true. */
  searchable?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function TokenSidePanel({
  groups,
  onInsert,
  className,
  showTokenString = true,
  searchable = true,
}: TokenSidePanelProps) {
  const [query, setQuery] = React.useState('');
  const searchId = React.useId();

  const filteredGroups = React.useMemo(() => {
    const q = query.trim();
    const withTokens = groups.filter((g) => g.tokens.length > 0);
    if (q === '') return withTokens;
    return withTokens
      .map((g) => ({
        ...g,
        tokens: g.tokens.filter(
          (t) =>
            fuzzyScore(q, t.label) != null ||
            fuzzyScore(q, t.token) != null ||
            fuzzyScore(q, t.description) != null,
        ),
      }))
      .filter((g) => g.tokens.length > 0);
  }, [groups, query]);

  return (
    <div
      className={cn(
        'flex flex-col w-56 shrink-0 min-h-0 overflow-hidden border-l border-border',
        'bg-gradient-to-b from-accent/30 to-accent/10',
        className,
      )}
    >
      {/* Panel header */}
      <div className="px-3 pt-3.5 pb-3 border-b border-border bg-primary/8 shrink-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <div className="flex items-center justify-center w-5 h-5 rounded bg-primary/15 text-primary shrink-0">
            <Braces className="w-3 h-3" />
          </div>
          <p className="text-xs font-semibold text-primary tracking-wide">
            Insert Field
          </p>
        </div>
        <p className="text-[10.5px] text-muted-foreground leading-snug pl-6">
          Click a field to insert it at the cursor
        </p>
        {searchable && (
          <div className="relative mt-2.5">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
            <Input
              id={searchId}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search fields…"
              aria-label="Search merge fields"
              className="h-7 pl-7 text-xs"
            />
          </div>
        )}
      </div>

      {/* Scrollable token list */}
      <ScrollArea className="flex-1 min-h-0">
        <ScrollAreaViewport>
        <ul
          role="listbox"
          aria-label="Merge fields"
          className="px-2 py-2.5 space-y-3.5"
        >
          {filteredGroups.map((group) => (
            <li key={group.label}>
              {/* Group heading */}
              <div className="flex items-center gap-1.5 px-1 mb-1.5">
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
                    group.accent === 'warning'
                      ? 'bg-muted text-muted-foreground'
                      : group.accent === 'muted'
                      ? 'bg-muted text-muted-foreground'
                      : 'bg-primary/12 text-primary',
                  )}
                >
                  {group.label}
                </span>
              </div>
              {group.description && (
                <p className="text-[10px] text-muted-foreground/70 leading-tight px-2 mb-1.5">
                  {group.description}
                </p>
              )}

              {/* Tokens */}
              <ul className="space-y-0.5" role="group" aria-label={group.label}>
                {group.tokens.map((item) => (
                  <li
                    key={item.token}
                    role="option"
                    aria-selected={false}
                    aria-disabled={item.dimmed || undefined}
                    aria-label={`${item.label}. ${item.description}`}
                  >
                    <button
                      type="button"
                      tabIndex={-1}
                      disabled={item.dimmed}
                      onClick={() => !item.dimmed && onInsert(item.token)}
                      className={cn(
                        'w-full text-left rounded-lg px-2.5 py-2 transition-all duration-150 group border',
                        item.dimmed
                          ? 'opacity-40 cursor-not-allowed border-transparent'
                          : 'border-transparent hover:border-primary/20 hover:bg-primary/8 hover:shadow-xs cursor-pointer',
                      )}
                    >
                      <div className="flex items-start gap-1.5">
                        {item.dimmed ? (
                          <Lock className="w-2.5 h-2.5 mt-0.5 shrink-0 text-muted-foreground/50" />
                        ) : (
                          <span className="w-1.5 h-1.5 rounded-full bg-primary/40 mt-1.5 shrink-0 group-hover:bg-primary transition-colors" />
                        )}
                        <div className="min-w-0">
                          <p
                            className={cn(
                              'text-xs font-semibold leading-tight truncate',
                              item.dimmed
                                ? 'text-muted-foreground'
                                : 'text-foreground group-hover:text-primary transition-colors',
                            )}
                          >
                            {item.label}
                          </p>
                          {showTokenString && (
                            <code className="block text-[10px] font-mono text-muted-foreground/70 leading-tight truncate mt-0.5">
                              {item.token}
                            </code>
                          )}
                          {item.description && (
                            <p className="text-[10px] text-muted-foreground leading-snug mt-0.5">
                              {item.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </li>
          ))}

          {filteredGroups.length === 0 && (
            <li className="text-xs text-muted-foreground px-1 py-2 list-none">
              {query.trim() ? `No fields match "${query.trim()}".` : 'No fields available.'}
            </li>
          )}
        </ul>
        </ScrollAreaViewport>
        <ScrollAreaScrollbar orientation="vertical" />
      </ScrollArea>
    </div>
  );
}
